import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Group as GroupIcon,
  CardTravel as CardTravelIcon,
  Timeline as TimelineIcon,
} from '@mui/icons-material';

// Supposons que ces actions sont définies dans les différents slices
import { fetchDashboardStats } from '../../features/admin/adminSlice';
import { fetchRecentPackages } from '../../features/packages/packagesSlice';

const DashboardPage = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  // Ces états proviendraient des slices correspondants
  const { stats, isLoading: statsLoading } = useSelector((state) => state.admin || { stats: {}, isLoading: false });
  const { packages, isLoading: packagesLoading } = useSelector((state) => state.packages);

  useEffect(() => {
    // Ces actions seraient définies dans les slices correspondants
    dispatch(fetchRecentPackages());
    dispatch(fetchDashboardStats());
  }, [dispatch]);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Tableau de bord
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Bienvenue, {user?.name}. Voici un aperçu de votre activité.
      </Typography>

      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Forfaits actifs */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140, position: 'relative' }}>
            {statsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <CardTravelIcon />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Forfaits Actifs
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {stats.activePackages || 0}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Vues totales */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}>
            {statsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <TimelineIcon />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Vues Totales
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {stats.totalViews || 0}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Taux de conversion ou autres statistiques */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}>
            {statsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <TrendingUpIcon />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    Taux de Conversion
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {stats.conversionRate ? `${stats.conversionRate}%` : '0%'}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Autres statistiques spécifiques au rôle */}
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', height: 140 }}>
            {statsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={30} />
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <GroupIcon />
                  </Avatar>
                  <Typography variant="h6" component="div">
                    {isSuperAdmin ? 'Compagnies Actives' : 'Clients Potentiels'}
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {isSuperAdmin ? (stats.activeCompanies || 0) : (stats.potentialClients || 0)}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Forfaits récents */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Forfaits Récents
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {packagesLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : packages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                  Aucun forfait récent à afficher
                </Typography>
              ) : (
                <List>
                  {packages.slice(0, 5).map((pkg) => (
                    <ListItem key={pkg.id} alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar src={pkg.image} alt={pkg.title}>
                          {pkg.title.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={pkg.title}
                        secondary={
                          <React.Fragment>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              {pkg.price.toLocaleString()} € • {pkg.days} jours
                            </Typography>
                            {` — ${pkg.views || 0} vues`}
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Activités récentes ou statistiques */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Activités Récentes
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {statsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {/* Graphique ou visualisation ici */}
                  <Box sx={{ height: 250, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      Graphique de visualisation des activités (à implémenter)
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;